import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  signInWithGoogle, signInWithMicrosoft, signInWithEmail, signUpWithEmail,
  resetPassword, signOut, resendVerification,
} from "../../auth/firebaseAuth";
import "./Signin.css";

// Map Firebase auth errors to friendly messages; null = benign (don't surface).
function friendlyError(err) {
  const code = err && err.code;
  const msg = (err && err.message) || "";
  switch (code) {
    case "auth/cancelled-popup-request": return null; // double-click
    case "auth/popup-closed-by-user": return "Sign-in was cancelled.";
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in window. Allow pop-ups and try again.";
    case "auth/network-request-failed":
      return "Network problem — check your connection and try again.";
    case "auth/invalid-email": return "That doesn't look like a valid email.";
    case "auth/missing-password": return "Enter your password.";
    case "auth/weak-password": return "Password should be at least 6 characters.";
    case "auth/email-already-in-use":
      return "An account with that email already exists — try signing in instead.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Wrong email or password.";
    case "auth/too-many-requests":
      return "Too many attempts — wait a moment and try again.";
    case "auth/account-exists-with-different-credential":
      return "You already have an account with this email — sign in with the method you used the first time (Google, Microsoft, or email/password).";
    default:
      if (/no invitation found/i.test(msg))
        return "This email hasn't been invited yet. Ask your administrator to invite you, then try again.";
      return "Something went wrong. Please try again.";
  }
}

function SignIn() {
  const auth = useSelector((state) => state.auth);
  // An invite link carries ?invite=<email>: pre-fill it and, if a *different*
  // account is already signed in on this browser, force switching to it.
  const invite = (new URLSearchParams(window.location.search).get("invite") || "").trim().toLowerCase();
  // Invitees have no account yet, so default them to "create account".
  const [mode, setMode] = useState(invite ? "signup" : "signin"); // 'signin' | 'signup'
  const [name, setName] = useState("");
  const [email, setEmail] = useState(invite);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const run = async (fn) => {
    setBusy(true); setError(null); setNotice(null);
    try { await fn(); }               // success → onAuthChange re-renders this screen
    catch (err) { const m = friendlyError(err); if (m) setError(m); }
    finally { setBusy(false); }
  };

  const submitEmail = (e) => {
    e.preventDefault();
    if (mode === "signup" && !name.trim()) { setError("Enter your name."); return; }
    if (!email || !password) { setError("Enter your email and password."); return; }
    run(() => (mode === "signup" ? signUpWithEmail(email, password, name) : signInWithEmail(email, password)));
  };

  const forgot = () => {
    if (!email) { setError("Enter your email above first, then tap “Forgot password?”."); return; }
    setBusy(true); setError(null); setNotice(null);
    const done = () => setNotice(`If an account exists for ${email}, we've emailed a reset link.`);
    resetPassword(email).then(done).catch(done).finally(() => setBusy(false));
  };

  const messages = (
    <>
      {error && <p className="si-msg si-error">{error}</p>}
      {notice && <p className="si-msg si-notice">{notice}</p>}
    </>
  );

  let title, body;
  if (auth === null) {
    title = "Mary's Bulletin Board";
    body = <p className="si-muted">Checking sign-in…</p>;
  } else if (auth === false) {
    title = mode === "signup" ? "Create your account" : "Sign in";
    body = (
      <>
        <p className="signin-sub">
          {invite
            ? (mode === "signup"
                ? <>Accept your invitation as <strong>{invite}</strong> — create a password below, or use Google/Microsoft.</>
                : <>Welcome back — sign in as <strong>{invite}</strong>.</>)
            : mode === "signup" ? "Set up your Care Circle." : "Welcome back — sign in to your board."}
        </p>
        <form onSubmit={submitEmail}>
          {mode === "signup" && (
            <input className="si-field" type="text" placeholder="Your name (e.g. Mark R.)" autoComplete="name"
                   value={name} onChange={(e) => setName(e.target.value)} />
          )}
          <input className="si-field" type="email" placeholder="Email" autoComplete="email"
                 value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="si-field" type="password"
                 placeholder={mode === "signup" ? "Choose a password" : "Password"}
                 autoComplete={mode === "signup" ? "new-password" : "current-password"}
                 value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" disabled={busy} className="si-btn si-btn-primary">
            {busy ? "Please wait…" : mode === "signup" ? "Create account" : "Sign in"}
          </button>
        </form>

        <div className="si-row">
          {mode === "signin" ? (
            <>
              <button className="si-link" onClick={forgot}>Forgot password?</button>
              <button className="si-link" onClick={() => { setMode("signup"); setError(null); }}>Create an account</button>
            </>
          ) : (
            <button className="si-link" onClick={() => { setMode("signin"); setError(null); }}>
              Already have an account? Sign in
            </button>
          )}
        </div>

        <div className="si-divider"><hr /> or <hr /></div>

        <button className="si-btn si-btn-oauth" disabled={busy} onClick={() => run(() => signInWithGoogle(invite || undefined))}>
          Continue with Google
        </button>
        <button className="si-btn si-btn-oauth" disabled={busy} onClick={() => run(() => signInWithMicrosoft(invite || undefined))}>
          Continue with Microsoft
        </button>

        {messages}
      </>
    );
  } else if (invite && auth.email && auth.email.toLowerCase() !== invite) {
    // A different account is signed in on this browser than the invite is for.
    title = "Switch account";
    body = (
      <>
        <p className="si-muted">You're signed in as <strong>{auth.email}</strong>.</p>
        <p className="si-msg si-info">
          This invitation is for <strong>{invite}</strong>. Sign out and sign in as {invite} to accept it.
        </p>
        <button className="si-btn si-btn-primary" onClick={() => signOut()}>
          Sign out &amp; continue as {invite}
        </button>
      </>
    );
  } else if (!auth.tenantId && auth.emailVerified === false) {
    title = "Verify your email";
    body = (
      <>
        <p className="si-muted">Signed in as <strong>{auth.email}</strong>.</p>
        <p className="si-msg si-info">
          Check your inbox for the verification link, click it, then reload this page to finish setting up your account.
        </p>
        <button className="si-btn si-btn-primary" disabled={busy}
          onClick={() => run(async () => { await resendVerification(); setNotice("Verification email sent."); })}>
          Resend verification email
        </button>
        <button className="si-btn si-btn-oauth" onClick={() => signOut()}>Sign out</button>
        {messages}
      </>
    );
  } else if (!auth.tenantId) {
    title = "Almost there";
    body = (
      <>
        <p className="si-muted">Signed in as <strong>{auth.email}</strong>.</p>
        <p className="si-msg si-info">
          Your account isn’t linked to a Care Circle yet. Ask the Care Circle member who set it up to invite you — or start your own Care Circle.
        </p>
        <Link to="/start" className="si-btn si-btn-primary" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>
          Start a care circle
        </Link>
        <button className="si-btn si-btn-oauth" onClick={() => signOut()}>Sign out</button>
      </>
    );
  } else {
    title = "You're signed in";
    body = (
      <>
        <p className="si-muted">Signed in as <strong>{auth.email}</strong>.</p>
        <Link to="/home" className="si-btn si-btn-primary" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>
          Go to your board
        </Link>
      </>
    );
  }

  return (
    <div className="signin-page">
      <div className="signin-brand">
        <div className="signin-brand-inner">
          <div className="signin-brand-logo">Mary's Bulletin Board</div>
          <p className="signin-brand-tag">Keeping residents <span className="grad">connected and cared for</span>.</p>
          <ul className="signin-points">
            <li>Post reminders, photos &amp; activities in seconds</li>
            <li>Residents see them on a simple, always-on display</li>
            <li>HIPAA-grade security &amp; data isolation</li>
          </ul>
        </div>
      </div>

      <div className="signin-main">
        <div className="signin-card">
          <div className="signin-card-logo">Mary's Bulletin Board</div>
          <h1 className="signin-title">{title}</h1>
          {body}
        </div>
      </div>
    </div>
  );
}

export default SignIn;
